import React, { FunctionComponent } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import Button from "Shared/Buttons/Button";
import styled from "styled-components";
import PlusIcon from "src/components/icons/Plus";
import { EMPTY_VALUE_DASH } from "src/constants";
import { localizeCurrencyToWholeNumber, prefixClassUtil } from "src/utils";
import useSelector from "src/utils/useSelector";
import { Accessory } from "Types/api/Accessory";
import GroupItem from "./GroupItem";
import { calculateTotal, Item } from "./lib";
import "./UpgradesReview.scss";

const AddButton = styled(Button)`
    background: none;
    border: 1px solid ${(props) => props.theme.palette.primary.main} !important;
    color: ${(props) => props.theme.palette.primary.main} !important;
    width: 100%;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: auto !important;
    }

    svg {
        color: ${(props) => props.theme.palette.primary.main} !important;
    }

    path {
        fill: ${(props) => props.theme.palette.primary.main} !important;
    }
`;

const PRE_NAME_CLASS = "ContinueDeal-UpgradesReview";
const prefixClass = prefixClassUtil(PRE_NAME_CLASS);

// TODO: temp
export interface ServiceAndProtection {
    id: number;
    thumbnail_url: string;
    description: string;
    price: number;
}

// TODO: temp
export interface GapInsurance {
    id: number;
    thumbnail_url: string;
    description: string;
    price: number;
}

interface DetailsItemProps {
    label: string;
    value: string;
}

const DetailsItem: FunctionComponent<DetailsItemProps> = ({ label, value }) => (
    <div className={prefixClass("details-item")}>
        <div className={prefixClass("details-item-label")}>{label}</div>
        <div className={prefixClass("details-item-value")}>{value}</div>
    </div>
);

export interface Props {
    accessories: Accessory[];
    services: ServiceAndProtection[];
    insurances: GapInsurance[];
    monthlyPayments: number;
    onlyShowAccessories?: boolean;

    onAccessoryRemove?(id: number): void;
    onServiceRemove?(id: number): void;
    onInsuranceRemove?(id: number): void;
    onAddMoreUpgrades?(): void;
}

const UpgradesReview: FunctionComponent<Props & WithTranslation> = ({
    accessories,
    services,
    insurances,
    monthlyPayments,
    onlyShowAccessories,
    onAccessoryRemove,
    onServiceRemove,
    onInsuranceRemove,
    onAddMoreUpgrades,
    t,
}) => {
    const upgradesLabel = useSelector((state) => state.account.account_disclaimers.upgrades_label);
    const total = calculateTotal([...(accessories as Item[]), ...services, ...insurances]);
    const totalValue = total ? localizeCurrencyToWholeNumber(total) : EMPTY_VALUE_DASH;
    const monthlyPaymentsValue = monthlyPayments ? localizeCurrencyToWholeNumber(monthlyPayments) : EMPTY_VALUE_DASH;

    return (
        <div className={PRE_NAME_CLASS}>
            <div className={prefixClass("main")}>
                <div className={prefixClass("groups")}>
                    <GroupItem
                        className={prefixClass("group")}
                        title={upgradesLabel}
                        items={accessories.map((m) => ({
                            ...m,
                            id: m.id,
                            img: m.thumbnail_url,
                            price: m.price,
                        }))}
                        onRemove={onAccessoryRemove}
                    />
                    {!onlyShowAccessories && (
                        <>
                            <GroupItem
                                className={prefixClass("group")}
                                title={t("ContinueDeal.UpgradesReview.serviceAndProtection")}
                                items={services.map((m) => ({
                                    ...m,
                                    img: m.thumbnail_url,
                                }))}
                                onRemove={onServiceRemove}
                            />
                            <GroupItem
                                className={prefixClass("group")}
                                title={t("ContinueDeal.UpgradesReview.gapInsurance")}
                                items={insurances.map((m) => ({
                                    ...m,
                                    img: m.thumbnail_url,
                                }))}
                                onRemove={onInsuranceRemove}
                            />
                        </>
                    )}
                </div>

                <div className={prefixClass("footer")}>
                    <AddButton onClick={onAddMoreUpgrades}>
                        {t("ContinueDeal.UpgradesReview.addMoreButtonLabel", { label: upgradesLabel })} &nbsp;&nbsp;
                        <PlusIcon />
                    </AddButton>

                    <div className={prefixClass("footer-details-column")}>
                        <DetailsItem label={t("ContinueDeal.UpgradesReview.total")} value={totalValue} />
                        <DetailsItem
                            label={t("ContinueDeal.UpgradesReview.monthlyPayment")}
                            value={monthlyPaymentsValue}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(UpgradesReview);
